// THE PAGE TITLE, DERIVED — one producer, outside React.
//
// Every entry in browser history read "TKT Back Office", because document.title was set exactly
// once at boot (brand.ts) and never again. With ~52 reachable routes and a detail page per
// entity, history and open tabs were unusable: nothing distinguished the order you were looking
// at from the payouts queue you opened before it.
//
// "Set it on each page" is 50 places to remember. Its milder cousin — an effect in the component
// that renders pages — is the same mistake with one hop of indirection, and it failed the same
// way within a week: /events and /events/:id became real file routes and stopped passing through
// that component, so the two most-visited pages silently went back to the brand name.
//
// So the title is not ASSIGNED by anything that renders. It is DERIVED, by one subscriber, from
// the only two inputs it has — and both already have exactly one writer:
//
//   document.title = titleFor(url, entityCache)
//        url         ← the router's location  (every route, present and future, by construction)
//        entityCache ← cacheEntity()          (the single write to the detail-entity store)
//
// Recomputed when either changes, and only then. Nothing to add to a new route, no dependency
// array to get wrong, no render involved: a page cannot forget a thing it was never asked to do.
import { NAV } from '../constants/nav';
import { BRAND } from './brand';
import { parsePathname, readCachedEntity, onEntityCached } from './router';

// Flattened once at module load, not per render. Sections may nest one level (`children`).
const NAV_LABELS = new Map();
for (const section of NAV || []) {
  for (const item of section?.items || []) {
    if (item?.id) NAV_LABELS.set(item.id, item.label);
    for (const child of item?.children || []) {
      if (child?.id) NAV_LABELS.set(child.id, child.label);
    }
  }
}

// WHAT TO CALL THE THING ON SCREEN — its NAME if it has one, otherwise the identifier a human
// would actually quote. That order matters and is not arbitrary: an event carries both, and its
// `displayId` is the URL slug ("venue-link-check-72168") while `name` is what anyone calls it
// ("Venue Link Check"). Reading the id first put the slug in the tab. A payout has only
// `displayId` (PAY-2026…), an order only `id` (its human reference, `routeId` being the uuid) —
// each lands on its own first present field. `customer` is last: a buyer's name describes an
// order but does not identify it, so it answers only when nothing else does. Anything that is
// not a non-empty string is skipped, so an object or a number can never reach the title bar.
const FIELDS = ['name', 'title', 'displayId', 'reference', 'code', 'id', 'customer'];
export const entityLabel = (entity) => {
  if (!entity || typeof entity !== 'object') return '';
  for (const field of FIELDS) {
    const value = entity[field];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
};

// LAST RESORT, AND THE REASON THIS CAN NEVER GO BLANK. 52 routes are reachable; only 32 are in
// NAV. The other 20 are detail pages (titled by their entity) plus a handful — finance, health,
// issues-approvals, season-passes, team, users — that are reachable by link and in no menu, so
// they had no label anywhere. Rather than add a second table for someone to forget, the route id
// itself is the fallback: it is guaranteed to exist, because it is what the router switched on.
// "season-passes" → "Season passes"; "order-detail" → "Order", since the entity's own name is the
// better answer there and this only shows in the moment before it loads.
const humanizeRoute = (active) => {
  const base = String(active || '').replace(/-detail$/, '').replace(/-/g, ' ').trim();
  return base ? base.charAt(0).toUpperCase() + base.slice(1) : '';
};

/** The document title for a route + its resolved entity. Pure. */
export function titleFor(active, entity) {
  // active === null means no route owns the URL (see router.parsePathname). Naming that is the
  // point: the tab used to read "Dashboard" for a dead link, matching the page it wrongly showed.
  if (!active) return `Not found · ${BRAND.name} Back Office`;
  const subject = entityLabel(entity) || NAV_LABELS.get(active) || humanizeRoute(active);
  const suffix = `${BRAND.name} Back Office`;
  return subject && subject !== suffix ? `${subject} · ${suffix}` : suffix;
}

/**
 * Bind document.title to the router. Called ONCE, next to createRouter (src/router.tsx) — before
 * React mounts, so the first paint already carries the right name.
 *
 * `onResolved` fires after a navigation commits AND its loaders have run, so a detail route that
 * fetched its entity (events.$id) is already in the cache by then. The legacy switch resolves its
 * entity after render instead, which is precisely what `onEntityCached` covers: a pasted order
 * link opens as "Order", then names itself the moment the order lands. Both paths, one rule.
 */
export function installPageTitle(router) {
  const apply = () => {
    const { active, id } = parsePathname(router.state.location.pathname);
    document.title = titleFor(active, readCachedEntity(active, id));
  };
  router.subscribe('onResolved', apply);
  onEntityCached(apply);
  apply();
}
